import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes } from "react-router-dom";
import ButtonGroup from "../../components/ButtonGroup";
import CustomTableSimple from "../../components/CustomTableSimple";
import ErrorMessage from "../../components/ErrorMessage";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import Main from "../../components/Main";
import SideMenu from "../../components/SideMenu";
import SimpleButton from "../../components/SimpleButton";
import Spacer from "../../components/Spacer";
import SubPage from "../../components/SubPage";
import SubPagesContainer from "../../components/SubPagesContainer";
import history from "../../providers/history";
import { IApplicationState } from "../../store";
import { systemAddDialog } from "../../store/system/slice";
import {
  itemsObservationsListRequest,
  startEditingItemObservation,
  itemObservationRemoveRequest,
} from "../../store/itemsObservations/slice";
import ItemObservationForm from "../ItemObservationForm";
import { Container, Content } from "./styles";
import { profileUserRequest } from "../../store/authentication/slice";

const ItemObservation: React.FC = () => {
  const itemsObservations = useSelector(
    (state: IApplicationState) => state.itemsObservations
  );

  const { t } = useTranslation();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(itemsObservationsListRequest())
    dispatch(profileUserRequest())
  }, [dispatch]);

  return (
    <Container id="container">
      <Header />

      <Content>
        <SideMenu />
        <Main>
          <h1>{t("pages.items_observations.title")}</h1>

          <Spacer size={20} />
          {itemsObservations.isLoading && (
            <>
              <p>{t("pages.items_observations.loading")}</p>
            </>
          )}


          <ButtonGroup>
            <SimpleButton
              color="primary"
              prependIcon="plus"
              onClick={() => {
                dispatch(
                  startEditingItemObservation({
                    observation: "",
                  })
                );
                history.push("item-observation/form");
              }}
            >
              {t("pages.items_observations.buttons.add_observation")}
            </SimpleButton>
          </ButtonGroup>
          <Spacer size={20} />

          {!itemsObservations.isLoading && !itemsObservations.error && itemsObservations.data.length === 0 && (
            <>
              <ErrorMessage>
                {t("pages.items_observations.not_found")}
              </ErrorMessage>
            </>
          )}


          <Spacer size={20} />

          {!itemsObservations.isLoading &&
            !itemsObservations.error &&
            itemsObservations.data.length > 0 && (
              <>
                <CustomTableSimple>
                  <thead>
                    <tr>
                      <th style={{ minWidth: 250 }}>
                        {t("pages.items_observations.observation")}
                      </th>
                      <th style={{ minWidth: 150, width: 150 }}>
                        {t("pages.items_observations.actions")}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {itemsObservations.data.map((itemObservation) => (
                      <tr key={`observation-id-${itemObservation.id}`}>
                        {itemObservation.isDeleting && (
                          <>
                            <td colSpan={3}>
                              {t("pages.items_observations.deleting")}
                            </td>
                          </>
                        )}

                        {!itemObservation.isDeleting && (
                          <>
                            <td>
                              {itemObservation.observation}
                              {itemObservation.error && (
                                <p>{itemObservation.error.message}</p>
                              )}
                            </td>
                            <td>
                              <ButtonGroup>
                                <SimpleButton
                                  size={"small"}
                                  prependIcon="pencil"
                                  color="primary"
                                  onClick={() => {
                                    dispatch(
                                      startEditingItemObservation(
                                        itemObservation
                                      )
                                    );
                                    history.push(
                                      `/settings/item-observation/${itemObservation.id}/form`
                                    );
                                  }}
                                >
                                  {t("common.buttons.edit")}
                                </SimpleButton>
                                <SimpleButton
                                  size={"small"}
                                  prependIcon="trash"
                                  color="danger"
                                  onClick={() => {
                                    return dispatch(
                                      systemAddDialog({
                                        title: t(
                                          "pages.items_observations.dialogs.remove_observation"
                                        ),
                                        message: t(
                                          "pages.items_observations.dialogs.remove_observation_message"
                                        ),
                                        actions: [
                                          {
                                            text: t(
                                              "pages.items_observations.buttons.remove_observation_confirm"
                                            ),
                                            action: () => {
                                              dispatch(
                                                itemObservationRemoveRequest(
                                                  itemObservation.id || ""
                                                )
                                              );

                                              history.push(
                                                `/settings/item-observation`
                                              );
                                            },
                                            color: "danger",
                                            prependIcon: "trash",
                                          },
                                          {
                                            text: t(
                                              "pages.items_observations.buttons.remove_observation_cancel"
                                            ),
                                            action: () => { },
                                            color: "none",
                                          },
                                        ],
                                      })
                                    );
                                  }}
                                >
                                  {t("common.buttons.delete")}
                                </SimpleButton>
                              </ButtonGroup>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </CustomTableSimple>

                <Spacer size={20} />
              </>
            )}
        </Main>
      </Content>

      <Routes>
        <Route
          path={"/:idObservation/form"}
          element={
            <SubPagesContainer>
              <SubPage>
                <ItemObservationForm />
              </SubPage>
            </SubPagesContainer>
          }
        />

        <Route
          path={"/form"}
          element={
            <SubPagesContainer>
              <SubPage>
                <ItemObservationForm />
              </SubPage>
            </SubPagesContainer>
          }
        />
      </Routes>

      <Footer />
    </Container>
  );
};

export default ItemObservation;
